import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/constants/branding";
import { Button } from "@/components/ui/button";

export default function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, updateCartItem, removeCartItem } = useStore();

  if (!user)
    return (
      <Empty
        title="Your cart is waiting"
        desc="Sign in to view items in your cart and checkout."
        action={<Button onClick={() => navigate("/login")} data-testid="cart-login">Sign In</Button>}
      />
    );

  if (!cart.items.length)
    return (
      <Empty
        title="Your cart is empty"
        desc="Looks like you haven't added anything yet."
        action={<Button onClick={() => navigate("/products")} data-testid="cart-shop">Start Shopping</Button>}
      />
    );

  const mrpTotal = cart.items.reduce((sum, it) => sum + (Number(it.mrp) > Number(it.price) ? Number(it.mrp) : Number(it.price)) * it.qty, 0);
  const savings = Math.max(0, mrpTotal - cart.subtotal);

  return (
    <div className="shell max-w-[1180px] py-5 sm:py-7 animate-fade-up">
      <div className="grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-5 items-start">
        <div className="surface overflow-hidden">
          <h1 className="font-display text-2xl font-bold text-deep px-5 sm:px-7 py-5 border-b border-border">My Cart ({cart.count})</h1>
          {cart.items.map((it) => (
            <div key={it.id} className="px-5 sm:px-7 py-5 border-b border-border" data-testid={`cart-item-${it.product_id}`}>
              <div className="flex gap-4">
                <div className="shrink-0 flex flex-col items-center gap-3">
                  <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-xl bg-secondary overflow-hidden cursor-pointer" onClick={() => navigate(`/products/${it.product_id}`)}>
                    <img src={it.image} alt={it.name} className="h-full w-full object-contain p-2 mix-blend-multiply" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-semibold line-clamp-2 cursor-pointer hover:text-primary" onClick={() => navigate(`/products/${it.product_id}`)}>{it.name}</h3>
                  <div className="mt-2 flex items-baseline flex-wrap gap-x-2">
                    {Number(it.mrp) > Number(it.price) && <span className="text-sm text-muted-foreground line-through">{formatINR(it.mrp)}</span>}
                    <span className="text-lg font-extrabold">{formatINR(it.price)}</span>
                    {Number(it.mrp) > Number(it.price) && (
                      <span className="text-xs font-extrabold text-success">{Math.round(((it.mrp - it.price) / it.mrp) * 100)}% off</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Line total: <span className="font-medium text-foreground">{formatINR(it.line_total)}</span></p>
                </div>
                <p className="hidden md:block text-xs text-muted-foreground shrink-0">Free delivery</p>
              </div>
              <div className="mt-4 flex items-center gap-5">
                <div className="flex items-center gap-2">
                  <button className="h-8 w-8 rounded-full border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors" onClick={() => updateCartItem(it.id, it.qty - 1)} data-testid={`cart-dec-${it.product_id}`} aria-label="Decrease"><Minus className="h-3.5 w-3.5" /></button>
                  <span className="w-10 text-center text-sm font-bold border border-border rounded-lg py-1">{it.qty}</span>
                  <button className="h-8 w-8 rounded-full border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors disabled:opacity-40" disabled={it.qty >= it.stock} onClick={() => updateCartItem(it.id, it.qty + 1)} data-testid={`cart-inc-${it.product_id}`} aria-label="Increase"><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <button className="text-sm font-semibold text-muted-foreground hover:text-destructive flex items-center gap-1 transition-colors" onClick={() => removeCartItem(it.id)} data-testid={`cart-remove-${it.product_id}`}>
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              </div>
            </div>
          ))}
          <div className="sticky bottom-0 bg-card px-5 sm:px-7 py-4 flex justify-end border-t border-border shadow-[0_-8px_20px_-12px_rgba(6,48,28,0.25)]">
            <button className="w-full sm:w-auto sm:px-14 h-12 rounded-xl bg-primary hover:bg-deep text-white font-bold text-sm transition-colors" onClick={() => navigate("/checkout")} data-testid="cart-checkout">
              Place Order
            </button>
          </div>
        </div>

        <div className="surface overflow-hidden lg:sticky lg:top-[130px]">
          <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground px-6 py-4 border-b border-border">Price Details</h2>
          <div className="px-6 py-5 space-y-4 text-sm">
            <Row label={`Price (${cart.count} items)`} value={formatINR(mrpTotal)} />
            {savings > 0 && <Row label="Discount" value={<span className="text-success">− {formatINR(savings)}</span>} />}
            <Row label="Delivery Charges" value={<span className="text-success">FREE</span>} />
          </div>
          <div className="mx-6 border-t border-dashed border-border py-4 flex items-center justify-between font-extrabold text-lg">
            <span>Total Amount</span>
            <span data-testid="cart-total">{formatINR(cart.subtotal)}</span>
          </div>
          {savings > 0 && (
            <p className="px-6 py-3.5 border-t border-border bg-primary/5 text-sm font-bold text-success">You will save {formatINR(savings)} on this order</p>
          )}
          <button className="w-full px-6 py-3.5 border-t border-border text-sm text-primary font-bold hover:bg-secondary transition-colors" onClick={() => navigate("/products")}>Continue Shopping</button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

const Empty = ({ title, desc, action }) => (
  <div className="shell py-10 sm:py-16">
    <div className="surface max-w-md mx-auto text-center py-14 px-6" data-testid="cart-empty">
      <div className="h-16 w-16 mx-auto mb-5 rounded-full bg-primary/10 flex items-center justify-center">
        <ShoppingBag className="h-8 w-8 text-primary" />
      </div>
      <h1 className="font-display text-2xl font-bold text-deep">{title}</h1>
      <p className="text-muted-foreground mt-2 mb-6">{desc}</p>
      {action}
    </div>
  </div>
);
